/* eslint-disable no-console */
import type { DocumentNotModified } from '@iconify/tools/lib/download/types/modified'
import type { FigmaImportNodeData } from '@iconify/tools/lib/import/figma/types/nodes'
import type { FigmaImportResult } from '@iconify/tools/lib/import/figma/types/result'
import type { FigmaIconsConfig, SyncResult } from './types'
import process from 'node:process'
import { cleanupSVG, IconSet, importFromFigma, isEmptyColor, parseColors, runSVGO } from '@iconify/tools'

interface IconNodeDetail {
  nodeId: string
  nodeName: string
  pageName: string
  path: string
}

function normalizeIconName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

function isValidIconName(name: string): boolean {
  return name.length > 0 && !name.startsWith('-')
}

function formatIconNameForLog(iconName: string): string {
  return iconName || '(empty)'
}

function getNodeDetail(node: FigmaImportNodeData): IconNodeDetail {
  const pageParent = node.parents.find(parent => parent.type === 'CANVAS')
  const pageName = pageParent?.name ?? '(unknown-page)'
  const path = [...node.parents.map(parent => parent.name), node.name].join(' / ')

  return {
    nodeId: node.id,
    nodeName: node.name,
    pageName,
    path,
  }
}

function collectIconNameDetail(
  iconNameDetailsMap: Map<string, IconNodeDetail[]>,
  iconName: string,
  node: FigmaImportNodeData,
): IconNodeDetail {
  const detail = getNodeDetail(node)
  const details = iconNameDetailsMap.get(iconName)

  if (details) {
    details.push(detail)
  }
  else {
    iconNameDetailsMap.set(iconName, [detail])
  }

  return detail
}

function collectValidIconNameDetail(
  iconNameDetailsMap: Map<string, IconNodeDetail[]>,
  node: FigmaImportNodeData,
): { iconName: string, detail: IconNodeDetail } | null {
  const iconName = normalizeIconName(node.name)

  if (!isValidIconName(iconName)) {
    const detail = getNodeDetail(node)
    console.log(`跳过非法图标名称: normalized="${formatIconNameForLog(iconName)}" | Node="${detail.nodeName}" | ID=${detail.nodeId} | Path=${detail.path}`)
    return null
  }

  return {
    iconName,
    detail: collectIconNameDetail(iconNameDetailsMap, iconName, node),
  }
}

function getDuplicateIconDetails(
  iconNameDetailsMap: Map<string, IconNodeDetail[]>,
): Array<[string, IconNodeDetail[]]> {
  return [...iconNameDetailsMap.entries()]
    .filter(([, details]) => details.length > 1)
    .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
}

function formatDuplicateIconErrorMessage(
  duplicateDetails: Array<[string, IconNodeDetail[]]>,
): string {
  const lines = [
    `检测到 ${duplicateDetails.length} 组重名图标，已终止同步。`,
    'Figma 允许重名，但同步时会按规范化名称生成 icon key（小写、空格转 -、移除特殊字符），重名会导致覆盖。',
    '请在 Figma 中改为唯一名称后重试。',
    '',
    '重名明细：',
  ]

  for (const [iconName, details] of duplicateDetails) {
    lines.push(`- "${iconName}" (${details.length} 个节点)`)
    details.forEach((detail, index) => {
      lines.push(`  ${index + 1}. Page="${detail.pageName}" | Node="${detail.nodeName}" | ID=${detail.nodeId} | Path=${detail.path}`)
    })
  }

  return lines.join('\n')
}

function assertNoDuplicateIconNames(iconNameDetailsMap: Map<string, IconNodeDetail[]>): void {
  const duplicateDetails = getDuplicateIconDetails(iconNameDetailsMap)
  if (!duplicateDetails.length) {
    return
  }
  throw new Error(formatDuplicateIconErrorMessage(duplicateDetails))
}

/**
 * 从 Figma 获取所有图标
 * 只调用一次 API，返回完整的 iconSet 和 page 映射信息
 */
export async function fetchAllIconsFromFigma(
  config: Omit<FigmaIconsConfig, 'pages' | 'convertToCurrentColor' | 'colorsToReplace'> & {
    allPages: string[]
  },
): Promise<{ iconSet: IconSet, pageMap: Map<string, string> } | null> {
  const {
    fileId,
    token,
    prefix,
    depth = 2,
    cacheDir = '.figma-cache',
    force = false,
    allPages,
    excludeNodeIds = [],
  } = config
  const excludedNodeIds = new Set(excludeNodeIds)

  // 如果强制同步，先删除缓存目录
  if (force) {
    const { rm } = await import('node:fs/promises')
    const { resolve } = await import('node:path')
    const cachePath = resolve(process.cwd(), cacheDir)
    try {
      await rm(cachePath, { recursive: true, force: true })
      console.log(`🗑️  已清除缓存目录: ${cacheDir}`)
    }
    catch (err) {
      // 缓存目录不存在也没关系
      console.error(`⚠️  清除缓存目录时出错: ${err}`)
    }
  }

  // 如果强制同步，将缓存 TTL 设置为 0 以跳过缓存
  const cacheAPITTL = force ? 0 : (config.cacheAPITTL ?? 1000 * 60 * 60 * 24 * 3) // 3 days
  const cacheSVGTTL = force ? 0 : (config.cacheSVGTTL ?? 1000 * 60 * 60 * 24 * 30) // 30 days

  console.log(`📦 从 Figma 获取图标 (一次性获取所有 pages)...`)
  console.log(`   文件 ID: ${fileId}`)
  console.log(`   前缀: ${prefix}`)
  console.log(`   目标 Pages: ${allPages.join(', ')}`)
  if (force) {
    console.log(`   ⚠️  强制同步模式：已清除所有缓存`)
  }

  // 用于记录每个图标所属的 page
  const pageMap = new Map<string, string>()
  const iconNameDetailsMap = new Map<string, IconNodeDetail[]>()

  // 从 Figma 导入所有图标
  const result: FigmaImportResult | DocumentNotModified = await importFromFigma({
    // 必需参数
    file: fileId,
    token,
    prefix,

    // 缓存配置
    cacheDir,
    cacheAPITTL,
    cacheSVGTTL,

    // 图标发现
    depth,

    // 获取所有图标，并记录它们所属的 page
    iconNameForNode: (node) => {
      if (excludedNodeIds.has(node.id)) {
        console.log(`跳过已排除图标节点: Node="${node.name}" | ID=${node.id}`)
        return null
      }

      // 排除顶层容器本身（Icons 和 Icons-Colored 的 Frame 或 Section）
      const hasContainerParent = node.parents.some(parent =>
        parent.type === 'FRAME' || parent.type === 'SECTION',
      )

      if (!hasContainerParent) {
        return null
      }

      // 查找图标所属的 page (CANVAS 类型的父节点)
      const pageParent = node.parents.find(parent => parent.type === 'CANVAS')
      if (!pageParent) {
        return null
      }

      // 检查是否在目标 pages 中
      if (allPages.length > 0 && !allPages.includes(pageParent.name)) {
        return null
      }

      const iconDetail = collectValidIconNameDetail(iconNameDetailsMap, node)
      if (!iconDetail) {
        return null
      }

      const { iconName, detail } = iconDetail

      // 记录图标所属的 page
      if (!pageMap.has(iconName)) {
        pageMap.set(iconName, detail.pageName)
      }

      return iconName
    },
  })

  // 处理未修改的情况
  if (typeof result === 'string') {
    console.log('✅ 图标未修改，跳过')
    return null
  }

  assertNoDuplicateIconNames(iconNameDetailsMap)

  const { iconSet } = result
  console.log(`📊 获取到 ${iconSet.count()} 个图标`)

  return { iconSet, pageMap }
}

/**
 * 过滤并处理 IconSet 中的图标
 * 根据 pages 过滤，应用颜色转换和 SVG 优化
 */
export async function filterAndProcessIcons(
  iconSet: IconSet,
  pageMap: Map<string, string>,
  options: {
    convertToCurrentColor?: boolean
    pages?: string[]
  } = {},
): Promise<IconSet> {
  const { convertToCurrentColor = true, pages = [] } = options

  // 创建一个新的 IconSet 用于存储过滤后的图标
  const newIconSet = new IconSet({
    prefix: iconSet.prefix,
    icons: {},
  })

  await iconSet.forEach(async (name: string, type: string) => {
    if (type !== 'icon') {
      return
    }

    if (!isValidIconName(name)) {
      return
    }

    // 根据 pages 过滤图标
    if (pages.length > 0) {
      const iconPage = pageMap.get(name)
      if (!iconPage || !pages.includes(iconPage)) {
        return
      }
    }

    const svg = iconSet.toSVG(name)
    if (!svg) {
      return
    }

    try {
      // 清理 SVG
      cleanupSVG(svg)

      // 先运行 SVGO 优化
      runSVGO(svg)

      // 然后处理颜色（在 SVGO 优化之后）
      if (convertToCurrentColor) {
        // 使用 Iconify 官方的 parseColors 函数处理颜色
        parseColors(svg, {
          defaultColor: 'currentColor',
          callback: (_attr, colorStr, color) => {
            // 如果颜色为空（如 fill="none"），保持原样
            if (!color || isEmptyColor(color)) {
              return colorStr
            }
            // 将所有其他颜色转换为 currentColor
            return 'currentColor'
          },
        })
      }

      // 将处理后的图标添加到新 IconSet
      newIconSet.fromSVG(name, svg)
    }
    catch (err) {
      console.error(`❌ 处理图标 ${name} 失败:`, err)
    }
  })

  return newIconSet
}

/**
 * 从 Figma 同步图标
 */
export async function syncIconsFromFigma(
  config: FigmaIconsConfig,
): Promise<SyncResult> {
  const {
    fileId,
    token,
    prefix,
    pages = [],
    depth = 2,
    convertToCurrentColor = true,
    cacheDir = '.figma-cache',
    force = false,
    excludeNodeIds = [],
  } = config
  const excludedNodeIds = new Set(excludeNodeIds)

  // 如果强制同步，先删除缓存目录
  if (force) {
    const { rm } = await import('node:fs/promises')
    const { resolve } = await import('node:path')
    const cachePath = resolve(process.cwd(), cacheDir)
    try {
      await rm(cachePath, { recursive: true, force: true })
      console.log(`🗑️  已清除缓存目录: ${cacheDir}`)
    }
    catch (err) {
      // 缓存目录不存在也没关系
      console.error(`⚠️  清除缓存目录时出错: ${err}`)
    }
  }

  // 如果强制同步，将缓存 TTL 设置为 0 以跳过缓存
  const cacheAPITTL = force ? 0 : (config.cacheAPITTL ?? 1000 * 60 * 60 * 24 * 3) // 3 days
  const cacheSVGTTL = force ? 0 : (config.cacheSVGTTL ?? 1000 * 60 * 60 * 24 * 30) // 30 days

  console.log(`📦 开始从 Figma 同步图标...`)
  console.log(`   文件 ID: ${fileId}`)
  console.log(`   前缀: ${prefix}`)
  if (pages.length > 0) {
    console.log(`   Pages: ${pages.join(', ')}`)
  }
  if (force) {
    console.log(`   ⚠️  强制同步模式：已清除所有缓存`)
  }

  const iconNameDetailsMap = new Map<string, IconNodeDetail[]>()

  // 从 Figma 导入图标
  const result: FigmaImportResult | DocumentNotModified = await importFromFigma({
    // 必需参数
    file: fileId,
    token,
    prefix,

    // 图标识别
    iconNameForNode: (node) => {
      if (excludedNodeIds.has(node.id)) {
        console.log(`跳过已排除图标节点: Node="${node.name}" | ID=${node.id}`)
        return null
      }

      // 如果指定了 pages 参数，只处理来自这些 Page 的图标
      if (pages.length > 0) {
        const isFromSpecifiedPage = node.parents.some(parent =>
          parent.type === 'CANVAS' && pages.includes(parent.name),
        )
        if (!isFromSpecifiedPage) {
          return null
        }
      }

      // 排除顶层容器本身（Icons 和 Icons-Colored 的 Frame 或 Section）
      // 检查是否有 Frame 或 Section 类型的父节点
      const hasContainerParent = node.parents.some(parent =>
        parent.type === 'FRAME' || parent.type === 'SECTION',
      )

      // 如果没有容器父节点，说明当前节点可能就是容器本身，需要排除
      if (!hasContainerParent) {
        return null
      }

      const iconDetail = collectValidIconNameDetail(iconNameDetailsMap, node)
      return iconDetail?.iconName ?? null
    },

    // 缓存配置
    cacheDir,
    cacheAPITTL,
    cacheSVGTTL,

    // 文档检索
    pages: pages.length > 0 ? pages : undefined,

    // 图标发现
    depth,

    // 不过滤父节点，接受所有图标
    // filterParentNode: undefined,
  })

  // 处理未修改的情况
  if (typeof result === 'string') {
    console.log('✅ 图标未修改，跳过')
    return {
      iconSet: null,
      count: 0,
      modified: false,
      icons: [],
    }
  }

  assertNoDuplicateIconNames(iconNameDetailsMap)

  const { iconSet } = result

  console.log(`📊 找到 ${iconSet.count()} 个图标`)

  // 处理每个图标
  await iconSet.forEach(async (name, type) => {
    if (type !== 'icon')
      return

    if (!isValidIconName(name)) {
      iconSet.remove(name)
      return
    }

    const svg = iconSet.toSVG(name)
    if (!svg) {
      iconSet.remove(name)
      return
    }

    try {
      // 清理 SVG
      cleanupSVG(svg)

      // 先运行 SVGO 优化
      runSVGO(svg)

      // 然后处理颜色（在 SVGO 优化之后）
      if (convertToCurrentColor) {
        // 使用 Iconify 官方的 parseColors 函数处理颜色
        // 这会自动：
        // 1. 转换已有的颜色为 currentColor
        // 2. 为没有颜色的元素添加 defaultColor
        parseColors(svg, {
          defaultColor: 'currentColor',
          callback: (_attr, colorStr, color) => {
            // 如果颜色为空（如 fill="none"），保持原样
            if (!color || isEmptyColor(color)) {
              return colorStr
            }
            // 将所有其他颜色转换为 currentColor
            return 'currentColor'
          },
        })
      }

      // 从 SVG 对象更新图标数据（包括颜色更改）
      iconSet.fromSVG(name, svg)
    }
    catch (err) {
      console.error(`❌ 处理图标 ${name} 失败:`, err)
      iconSet.remove(name)
    }
  })

  const icons = iconSet.list()
  console.log(`✅ 成功处理 ${icons.length} 个图标`)

  return {
    iconSet,
    count: icons.length,
    modified: true,
    icons,
  }
}
